import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { FolderViewWrapper } from "../../../src/taskpane/components/helpers/folderViewWrapper";
import { SharePointFolderContentTypeIds } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { GridProps, IGridGroup } from "../useContentManagerBody";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { Delete20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/Delete20Regular";

export function useMenuItem_Delete(
  selectedItems: any[],
  waitFor: boolean,
  setWaitFor: (waitFor: boolean) => void,
  setShowDeleteDialog: (showDeleteDialog: boolean) => void,
  gridProps: GridProps,
  setGridProps: (newGridProps: GridProps) => void,
  setSelectionCleared: (selectionCleared: boolean) => void,
) {
  const languageStrings = React.useContext(LanguageContext);

  // --- Redux Selectors ---
  const siteInfo = useSelector((state: RootState) => state.tenantInfo.sites);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.at(-1));

  // --- Memoized Menu Item ---
  const menuItem_Delete = React.useMemo(() => {
    const hasSelection = selectedItems?.length > 0;
    if (!hasSelection) {
      return null;
    }

    const userCanDeleteAll = !selectedItems.some(x => x?.EffectiveBasePermissions?.canDeleteListItem === false);
    if (!userCanDeleteAll) {
      return null;
    }

    // Encapsulate folder permission check
    const folderViewWrapper = new FolderViewWrapper(siteInfo, currentDirectory);
    const canDeleteFolders = folderViewWrapper.canViewFolder();
    const containsFolders = selectedItems.some(x => x.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder));

    // The user can delete if they have folder permissions, OR if the selection contains no folders.
    const shouldShowMenuItem = canDeleteFolders || !containsFolders;

    if (shouldShowMenuItem) {
      return {
        key: "delete",
        disabled: waitFor === true,
        text: languageStrings.Delete,
        // iconProps: { iconName: "Delete" },
        iconProps: {},
        onRenderIcon: () => <Delete20Regular className={menuItemIconStyles} />,
        onClick: () => {
          setShowDeleteDialog(true);
        }
      };
    }

    return null;

  }, [selectedItems, waitFor, siteInfo, currentDirectory, languageStrings, setShowDeleteDialog]);

  const deleteSuccess = React.useCallback((successIds: string[]) => {
    setShowDeleteDialog(false);
    if (!successIds || successIds.length === 0 || !gridProps?.items) {
      return;
    }

    try {
      setWaitFor(true);

      const newRows = gridProps.items.filter(item => item && !successIds.includes(item.ID));

      let newGroups: IGridGroup[] = [];

      if (gridProps.groups?.length > 0) {

        const deletedIndexes = new Set<number>();
        gridProps.items.forEach((item, index) => {
          if (item && successIds.includes(item.ID)) {
            deletedIndexes.add(index);
          }
        });

        // Recalculate group properties based on the original groups
        newGroups = gridProps.groups.map(group => {
          // Count how many deleted items were INSIDE this group.
          const deletedItemsWithin = Array.from(deletedIndexes).filter(index => index >= group.startIndex && index < group.startIndex + group.count).length;
          const newCount = group.count - deletedItemsWithin;

          if (newCount <= 0) return null;

          // Count how many deleted items were BEFORE this group started.
          // This determines the new startIndex.
          const deletedItemsBefore = Array.from(deletedIndexes).filter(index => index < group.startIndex).length;
          const newStartIndex = group.startIndex - deletedItemsBefore;

          // The total count for the group's name also needs updating
          const newTotalCount = group.totalCount - deletedItemsWithin;
          const newName = group.name.replace(/\(\d+\)/, `(${newTotalCount})`);

          return {
            ...group,
            startIndex: newStartIndex,
            count: newCount,
            totalCount: newTotalCount,
            name: newName,
          };
        }).filter(Boolean);
      }

      setGridProps({
        ...gridProps,
        items: newRows,
        groups: newGroups,
      });

      setSelectionCleared(true);

    } catch (error) {
      logger(error, "useMenuItem_Delete.tsx (filesDeleted)");
    } finally {
      setWaitFor(false);
    }

  }, [gridProps, setGridProps, setShowDeleteDialog, setSelectionCleared, setWaitFor]);

  return {
    menuItem_Delete,
    deleteSuccess
  };
}